import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio",
  "Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming","District of Columbia"
];

export function useDisclosureGate() {
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    const stored = localStorage.getItem("disclosure_acknowledged");
    if (stored) setAcknowledged(true);
  }, []);
  const markAcknowledged = () => {
    localStorage.setItem("disclosure_acknowledged", "true");
    setAcknowledged(true);
  };
  return { acknowledged, markAcknowledged };
}

export default function DisclosureGate({ open, onComplete, onClose }) {
  const [state, setState] = useState("");
  const [checked, setChecked] = useState(false);
  const [step, setStep] = useState("state"); // "state" | "disclosure"

  const isNY = state === "New York";
  const isFL = state === "Florida";

  const handleContinue = () => {
    if (!state) return;
    setStep("disclosure");
  };

  const handleAcknowledge = async () => {
    if (!checked) return;
    try {
      await axios.post(`${API}/disclosures/acknowledge`, {
        state,
        disclosure_type: isNY ? "ny_agency" : isFL ? "fl_brokerage" : "general",
        acknowledged: true,
      }, { withCredentials: true });
    } catch (err) {
      logger.error("Failed to log disclosure:", err);
    }
    localStorage.setItem("disclosure_acknowledged", "true");
    localStorage.setItem("disclosure_state", state);
    onComplete();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="disclosure-gate"
      >
        <motion.div
          className="bg-white rounded-xl max-w-lg w-full shadow-2xl overflow-hidden"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
        >
          {/* Header */}
          <div className="bg-[#1A1A2E] px-6 py-4 flex items-center gap-3">
            <Shield className="w-5 h-5 text-[#0066FF]" />
            <h2 className="text-white font-semibold text-lg">
              {step === "state" ? "Before We Proceed" : "Required Disclosure"}
            </h2>
          </div>

          <div className="p-6">
            {step === "state" ? (
              /* Step 1: State Selection */
              <div data-testid="disclosure-state-step">
                <p className="text-slate-600 mb-4">
                  Please review a required disclosure before we discuss specific properties. Select your state to continue.
                </p>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Your State *</label>
                  <Select value={state} onValueChange={setState}>
                    <SelectTrigger className="h-12" data-testid="disclosure-state-select">
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 justify-end">
                  {onClose && (
                    <Button variant="ghost" onClick={onClose} data-testid="disclosure-cancel-btn">Cancel</Button>
                  )}
                  <Button
                    onClick={handleContinue}
                    disabled={!state}
                    className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
                    data-testid="disclosure-continue-btn"
                  >
                    Continue <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            ) : (
              /* Step 2: Disclosure */
              <div data-testid="disclosure-acknowledge-step">
                {isNY ? (
                  <div>
                    <h3 className="font-semibold text-[#1A1A2E] mb-3">Agency Disclosure — New York</h3>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 text-sm text-slate-700 leading-relaxed">
                      <p className="mb-3">In accordance with New York State law, real estate licensees are required to provide an Agency Disclosure Form at the time of first substantive contact.</p>
                      <p className="mb-3"><strong>Krieger Real Estate LLC represents buyers in most transactions.</strong></p>
                      <p className="mb-2">In limited off-market resale situations, the firm may represent a seller. In such cases:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Seller representation will be disclosed</li>
                        <li>Dual agency, if applicable, will be disclosed in writing and requires informed written consent from all parties in accordance with New York law</li>
                        <li>A designated agent will be appointed where required</li>
                      </ul>
                    </div>
                  </div>
                ) : isFL ? (
                  <div>
                    <h3 className="font-semibold text-[#1A1A2E] mb-3">Brokerage Relationship Disclosure — Florida</h3>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 text-sm text-slate-700 leading-relaxed">
                      <p className="mb-3"><strong>Krieger Real Estate LLC operates as a Transaction Broker in Florida, representing buyers. We do not represent sellers or enter into dual agency relationships in Florida.</strong></p>
                      <p className="mb-3">We represent buyers only in Florida and do not represent sellers or enter into dual agency relationships in Florida.</p>
                      <p>Disclosure will be provided prior to substantive discussion.</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-semibold text-[#1A1A2E] mb-3">Brokerage Disclosure</h3>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 text-sm text-slate-700 leading-relaxed">
                      <p className="mb-3"><strong>Krieger Real Estate LLC is a licensed real estate brokerage representing buyers in the acquisition of condominium units.</strong></p>
                      <p className="mb-3">We assist with property identification, offer preparation and presentation, and negotiation with sponsors, developers, or unit owners.</p>
                      <p>This website does not constitute an offer to sell securities. No investment advice is provided.</p>
                    </div>
                  </div>
                )}

                <label className="flex items-start gap-3 cursor-pointer mb-6" data-testid="disclosure-checkbox-label">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setChecked(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-[#0066FF] focus:ring-[#0066FF]"
                    data-testid="disclosure-checkbox"
                  />
                  <span className="text-sm text-slate-700">
                    {isNY
                      ? "I acknowledge receipt of the New York Agency Disclosure."
                      : isFL
                      ? "I acknowledge the brokerage relationship disclosure."
                      : "I acknowledge the brokerage disclosure."}
                  </span>
                </label>

                <div className="flex gap-3 justify-end">
                  <Button variant="ghost" onClick={() => { setStep("state"); setChecked(false); }} data-testid="disclosure-back-btn">
                    Back
                  </Button>
                  <Button
                    onClick={handleAcknowledge}
                    disabled={!checked}
                    className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
                    data-testid="disclosure-acknowledge-btn"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
